import React from 'react';
import { FaChevronDown, FaChevronUp, FaChartBar, FaUsers, FaEuroSign, FaPercentage } from 'react-icons/fa';

const BusinessAnalysisCard = ({ analysis, expanded = false, onToggle }) => {
    const formatMetric = (value, type = 'number') => {
        if (type === 'currency') {
            return `€${value.toFixed(2)}`;
        }
        if (type === 'percentage') {
            return `${value}%`;
        }
        return value.toLocaleString();
    };

    const getMetricIcon = (metric) => {
        switch (metric) {
            case 'totalSales':
                return <FaEuroSign className="text-green-500" />;
            case 'totalCustomers':
                return <FaUsers className="text-blue-500" />;
            case 'averageTicket':
                return <FaChartBar className="text-purple-500" />;
            case 'retentionRate':
                return <FaPercentage className="text-orange-500" />;
            default:
                return <FaChartBar className="text-gray-500" />;
        }
    };

    const getMetricLabel = (metric) => {
        switch (metric) {
            case 'totalSales':
                return 'Ventas Totales';
            case 'totalCustomers':
                return 'Clientes Totales';
            case 'averageTicket':
                return 'Ticket Promedio';
            case 'retentionRate':
                return 'Tasa de Retención';
            default:
                return metric;
        }
    };

    const getTrendColor = (trend) => {
        switch (trend?.toLowerCase()) {
            case 'growing':
            case 'positive':
                return 'bg-green-100 text-green-800';
            case 'declining':
            case 'negative':
                return 'bg-red-100 text-red-800';
            case 'stable':
                return 'bg-blue-100 text-blue-800';
            case 'critical':
                return 'bg-red-100 text-red-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    return (
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            {/* Header */}
            <div
                className="bg-gradient-to-r from-blue-500 to-purple-600 p-6 cursor-pointer"
                onClick={onToggle}
            >
                <div className="flex items-center justify-between">
                    <div className="flex items-center">
                        <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center mr-4">
                            <FaChartBar className="text-white text-xl" />
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-white">
                                Análisis del Negocio
                            </h3>
                            <p className="text-blue-100">
                                Resumen ejecutivo basado en tus datos
                            </p>
                        </div>
                    </div>
                    <div className="text-white">
                        {expanded ? <FaChevronUp /> : <FaChevronDown />}
                    </div>
                </div>
            </div>

            {/* Summary */}
            <div className="p-6 border-b border-gray-200">
                <p className="text-gray-700 leading-relaxed">
                    {analysis.summary}
                </p>
            </div>

            {/* Key Metrics */}
            <div className="p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">
                    Métricas Clave
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {analysis.keyMetrics && Object.entries(analysis.keyMetrics).map(([key, value]) => (
                        <div key={key} className="bg-gray-50 rounded-lg p-4 text-center">
                            <div className="flex justify-center mb-2">
                                {getMetricIcon(key)}
                            </div>
                            <div className="text-2xl font-bold text-gray-900">
                                {typeof value === 'object' ? (
                                    <div className="text-sm">
                                        {Object.entries(value).map(([k, v]) => (
                                            <div key={k} className="mb-1">
                                                <span className="font-medium">{k}:</span> {v}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    formatMetric(value, key === 'averageTicket' ? 'currency' : 'number')
                                )}
                            </div>
                            <div className="text-sm text-gray-600">
                                {getMetricLabel(key)}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Temporal Analysis Comparison */}
            {analysis.temporalAnalysis && (
                <div className="p-6 border-t border-gray-200">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">
                        Comparación Temporal
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Current Period */}
                        <div className="bg-blue-50 rounded-lg p-4">
                            <h5 className="font-semibold text-blue-900 mb-3 flex items-center">
                                <span className="w-3 h-3 bg-blue-500 rounded-full mr-2"></span>
                                Período Actual: {analysis.temporalAnalysis.currentPeriod?.period || 'N/A'}
                            </h5>
                            <div className="space-y-2">
                                <div className="flex justify-between">
                                    <span className="text-sm text-blue-700">Ventas:</span>
                                    <span className="font-medium">{formatMetric(analysis.temporalAnalysis.currentPeriod?.sales || 0, 'currency')}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-sm text-blue-700">Clientes:</span>
                                    <span className="font-medium">{analysis.temporalAnalysis.currentPeriod?.totalCustomers || 0}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-sm text-blue-700">Nuevos Clientes:</span>
                                    <span className="font-medium">{analysis.temporalAnalysis.currentPeriod?.newCustomers || 0}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-sm text-blue-700">Ticket Promedio:</span>
                                    <span className="font-medium">{formatMetric(analysis.temporalAnalysis.currentPeriod?.averageTicket || 0, 'currency')}</span>
                                </div>
                            </div>
                        </div>

                        {/* Previous Period */}
                        <div className="bg-green-50 rounded-lg p-4">
                            <h5 className="font-semibold text-green-900 mb-3 flex items-center">
                                <span className="w-3 h-3 bg-green-500 rounded-full mr-2"></span>
                                Período Anterior: {analysis.temporalAnalysis.previousPeriod?.period || 'N/A'}
                            </h5>
                            <div className="space-y-2">
                                <div className="flex justify-between">
                                    <span className="text-sm text-green-700">Ventas:</span>
                                    <span className="font-medium">{formatMetric(analysis.temporalAnalysis.previousPeriod?.sales || 0, 'currency')}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-sm text-green-700">Clientes:</span>
                                    <span className="font-medium">{analysis.temporalAnalysis.previousPeriod?.totalCustomers || 0}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-sm text-green-700">Nuevos Clientes:</span>
                                    <span className="font-medium">{analysis.temporalAnalysis.previousPeriod?.newCustomers || 0}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-sm text-green-700">Ticket Promedio:</span>
                                    <span className="font-medium">{formatMetric(analysis.temporalAnalysis.previousPeriod?.averageTicket || 0, 'currency')}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Growth Rates */}
                    {analysis.temporalAnalysis.growthRates && (
                        <div className="mt-4 bg-gray-50 rounded-lg p-4">
                            <h5 className="font-semibold text-gray-900 mb-3">Tasas de Crecimiento</h5>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div className="text-center">
                                    <div className={`text-lg font-bold ${analysis.temporalAnalysis.growthRates.sales >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                        {analysis.temporalAnalysis.growthRates.sales >= 0 ? '+' : ''}{analysis.temporalAnalysis.growthRates.sales}%
                                    </div>
                                    <div className="text-sm text-gray-600">Ventas</div>
                                </div>
                                <div className="text-center">
                                    <div className={`text-lg font-bold ${analysis.temporalAnalysis.growthRates.newCustomers >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                        {analysis.temporalAnalysis.growthRates.newCustomers >= 0 ? '+' : ''}{analysis.temporalAnalysis.growthRates.newCustomers}%
                                    </div>
                                    <div className="text-sm text-gray-600">Nuevos Clientes</div>
                                </div>
                                <div className="text-center">
                                    <div className={`text-lg font-bold ${analysis.temporalAnalysis.growthRates.averageTicket >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                        {analysis.temporalAnalysis.growthRates.averageTicket >= 0 ? '+' : ''}{analysis.temporalAnalysis.growthRates.averageTicket}%
                                    </div>
                                    <div className="text-sm text-gray-600">Ticket Promedio</div>
                                </div>
                                <div className="text-center">
                                    <div className={`text-lg font-bold ${analysis.temporalAnalysis.growthRates.totalCustomers >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                        {analysis.temporalAnalysis.growthRates.totalCustomers >= 0 ? '+' : ''}{analysis.temporalAnalysis.growthRates.totalCustomers}%
                                    </div>
                                    <div className="text-sm text-gray-600">Total Clientes</div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Trends */}
                    {analysis.temporalAnalysis.trends && (
                        <div className="mt-4 bg-yellow-50 rounded-lg p-4">
                            <h5 className="font-semibold text-yellow-900 mb-3">Tendencias</h5>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div className="text-center">
                                    <div className={`text-sm font-medium px-2 py-1 rounded ${getTrendColor(analysis.temporalAnalysis.trends.overall)}`}>
                                        {analysis.temporalAnalysis.trends.overall}
                                    </div>
                                    <div className="text-xs text-gray-600 mt-1">General</div>
                                </div>
                                <div className="text-center">
                                    <div className={`text-sm font-medium px-2 py-1 rounded ${getTrendColor(analysis.temporalAnalysis.trends.sales)}`}>
                                        {analysis.temporalAnalysis.trends.sales}
                                    </div>
                                    <div className="text-xs text-gray-600 mt-1">Ventas</div>
                                </div>
                                <div className="text-center">
                                    <div className={`text-sm font-medium px-2 py-1 rounded ${getTrendColor(analysis.temporalAnalysis.trends.customers)}`}>
                                        {analysis.temporalAnalysis.trends.customers}
                                    </div>
                                    <div className="text-xs text-gray-600 mt-1">Clientes</div>
                                </div>
                                <div className="text-center">
                                    <div className={`text-sm font-medium px-2 py-1 rounded ${getTrendColor(analysis.temporalAnalysis.trends.health)}`}>
                                        {analysis.temporalAnalysis.trends.health}
                                    </div>
                                    <div className="text-xs text-gray-600 mt-1">Salud</div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Expanded Content */}
            {expanded && (
                <div className="border-t border-gray-200">
                    {/* Patterns */}
                    {analysis.patterns && (
                        <div className="p-6 border-b border-gray-200">
                            <h4 className="text-lg font-semibold text-gray-900 mb-4">
                                Patrones Identificados
                            </h4>
                            <div className="space-y-4">
                                {analysis.patterns.seasonalTrends && (
                                    <div>
                                        <h5 className="font-medium text-gray-800 mb-2">Tendencias Estacionales</h5>
                                        <p className="text-gray-600 text-sm">
                                            {analysis.patterns.seasonalTrends.highSeason && (
                                                <span className="block mb-1">
                                                    <strong>Alta temporada:</strong> {analysis.patterns.seasonalTrends.highSeason}
                                                </span>
                                            )}
                                            {analysis.patterns.seasonalTrends.lowSeason && (
                                                <span className="block">
                                                    <strong>Baja temporada:</strong> {analysis.patterns.seasonalTrends.lowSeason}
                                                </span>
                                            )}
                                        </p>
                                    </div>
                                )}

                                {analysis.patterns.weeklyPatterns && (
                                    <div>
                                        <h5 className="font-medium text-gray-800 mb-2">Patrones Semanales</h5>
                                        <p className="text-gray-600 text-sm">{analysis.patterns.weeklyPatterns}</p>
                                    </div>
                                )}

                                {analysis.patterns.customerBehavior && (
                                    <div>
                                        <h5 className="font-medium text-gray-800 mb-2">Comportamiento del Cliente</h5>
                                        <p className="text-gray-600 text-sm">{analysis.patterns.customerBehavior}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Opportunities */}
                    {analysis.opportunities && analysis.opportunities.length > 0 && (
                        <div className="p-6 border-b border-gray-200">
                            <h4 className="text-lg font-semibold text-gray-900 mb-4">
                                Oportunidades Identificadas
                            </h4>
                            <ul className="space-y-2">
                                {analysis.opportunities.map((opportunity, index) => (
                                    <li key={index} className="flex items-start">
                                        <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                                        <span className="text-gray-700">{opportunity}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Recommendations */}
                    {analysis.recommendations && analysis.recommendations.length > 0 && (
                        <div className="p-6">
                            <h4 className="text-lg font-semibold text-gray-900 mb-4">
                                Recomendaciones
                            </h4>
                            <ul className="space-y-2">
                                {analysis.recommendations.map((recommendation, index) => (
                                    <li key={index} className="flex items-start">
                                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                                        <span className="text-gray-700">{recommendation}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default BusinessAnalysisCard; 